import { Alignment, UseTransitionStylesProps } from "@floating-ui/react";

import {
  createContext,
  HTMLAttributes,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";

import { v4 } from "uuid";

export type ToastPlacement =
  | "top"
  | "bottom"
  | `top-${Alignment}`
  | `bottom-${Alignment}`;

export type Id = string;

export type ToastType = {
  id: Id;
  delay?: number;
  autoClose?: boolean;
  requestClose?: boolean;
  transition?: UseTransitionStylesProps;
  placement?: ToastPlacement;
  render?: (id: Id, onClose?: () => void) => ReactNode;
};

export type ToastsType = Array<ToastType>;

export type ContextType = {
  listRef: MutableRefObject<Array<HTMLElement | null>>;
  toasts: ToastsType;
  placements: Set<ToastPlacement>;
  toast: (toast: Omit<ToastType, "id">) => void;
  close: (toastId: Id) => void;
  closeAll: () => void;
};

export interface ProviderType extends Partial<ToastType> {
  children: ReactNode;
}

export type ToastContentProps = Required<ToastType> &
  HTMLAttributes<HTMLLIElement>;

export function useToasts() {
  const [toasts, setToasts] = useState<ToastsType>([]);
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const [placements, setPlacements] = useState(new Set<ToastPlacement>([]));

  const toast = useCallback(
    ({
      placement,
      delay,
      transition,
      autoClose,
      render,
    }: Omit<ToastType, "id">) => {
      setPlacements((prev) => {
        if (!placement) {
          return new Set(prev);
        }

        const placements = new Set(prev);
        placements.add(placement);
        return placements;
      });
      setToasts((prev) => [
        ...prev,
        {
          id: v4(),
          placement,
          delay,
          transition,
          autoClose,
          render,
        },
      ]);
    },
    [],
  );

  const close = useCallback((toastId: Id) => {
    setToasts((prev) => {
      const t = prev.map((toast) => {
        if (toast.id === toastId) {
          return {
            ...toast,
            requestClose: true,
          };
        }
        return toast;
      });
      return t;
    });
  }, []);

  const closeAll = useCallback(() => {
    const t = toasts.map((toast) => ({
      ...toast,
      requestClose: true,
    }));
    setToasts(t);
  }, [toasts]);

  return useMemo(
    () => ({
      listRef,
      toasts,
      placements,
      toast,
      close,
      closeAll,
    }),
    [listRef, toasts, placements, toast, close, closeAll],
  );
}

export const ToastContext = createContext<ContextType>(null as any);

export const useToast = () => {
  const context = useContext(ToastContext);

  if (context == null) {
    throw new Error("Toast components must be wrapped in <ToastProvider />");
  }

  return context;
};

export const getToastPosition = (placement?: ToastPlacement) => {
  if (placement == null) return {};

  const placements = placement.split("-");
  if (placements[0] === "top" || placements[0] === "bottom") {
    return {
      margin: "0 auto",
      top: placements[0] === "top" ? "20px" : undefined,
      bottom: placements[0] === "bottom" ? "20px" : undefined,
      left:
        placements[1] === undefined
          ? 0
          : placements[1] === "start"
            ? "20px"
            : "auto",
      right:
        placements[1] === undefined
          ? 0
          : placements[1] === "end"
            ? "20px"
            : "auto",
    };
  }

  return {};
};

export const useTimeout = (fn: (...args: any) => void, duration: number) => {
  const [isRunning, setIsRunning] = useState(true);
  const onTimeout = useEffectEvent(fn);

  const start = useCallback(() => {
    setIsRunning(true);
  }, []);

  const stop = useCallback(() => {
    setIsRunning(false);
  }, []);

  useEffect(() => {
    if (isRunning) {
      const timerId = setTimeout(() => {
        onTimeout();
      }, duration);

      return () => clearTimeout(timerId);
    }
  }, [duration, isRunning]);

  return {
    start,
    stop,
  };
};
